/**
 * DonutCharts Component JavaScript
 * From Astro reference - uses Chart.js with datalabels plugin
 */
import { Chart, DoughnutController, ArcElement, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register only what we need for smaller bundle
Chart.register(DoughnutController, ArcElement, Tooltip, ChartDataLabels);

// Define base colors
const DONUT_COLORS = [
    "#D71414", // Brand red
    "#F34D23",
    "#C5BDB7",
    "#7E706A",
    "#211918",
];

function getChartColors(dataLength) {
    const colors = [...DONUT_COLORS];
    while (colors.length < dataLength) {
        const baseIndex = (colors.length - DONUT_COLORS.length) % DONUT_COLORS.length;
        colors.push(DONUT_COLORS[baseIndex] + "99");
    }
    return colors.slice(0, dataLength);
}

// Store chart instances for legend hover interaction
const chartInstances = new Map();
// Track canvases that have been initialized (for viewport animation)
const initializedCanvases = new WeakSet();
// Track canvases being observed
const observedCanvases = new WeakSet();

// IntersectionObserver for viewport-triggered animations
const chartObserver = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const canvas = entry.target;
                // Only init once when entering viewport
                if (!initializedCanvases.has(canvas)) {
                    initSingleChart(canvas);
                    initializedCanvases.add(canvas);
                }
                // Stop observing after init
                chartObserver.unobserve(canvas);
            }
        });
    },
    {
        root: null,
        rootMargin: "50px",
        threshold: 0.1,
    }
);

function initSingleChart(canvas, chartIndex = 0) {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const chartDataAttr = canvas.getAttribute("data-chart-data");
    if (!chartDataAttr) return;

    let chartData;
    try {
        chartData = JSON.parse(chartDataAttr);
    } catch (e) {
        console.error("Invalid chart data JSON:", e);
        return;
    }

    if (!Array.isArray(chartData) || chartData.length === 0) return;

    // Destroy existing chart if it exists
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
        existingChart.destroy();
    }

    const colors = getChartColors(chartData.length);

    // Detect consecutive small slice runs and find the longest one
    // to decide label strategy:
    //   - No adjacent small slices: regular labels (all outside)
    //   - Up to 3 adjacent small slices: alternate inside/outside donut hole
    //   - More than 3 adjacent small slices: hide labels, show tooltips
    const total = chartData.reduce((sum, d) => sum + d.value, 0);
    let maxRunLength = 0;
    let currentRun = 0;
    for (let i = 0; i < chartData.length; i++) {
        const pct = (chartData[i].value / total) * 100;
        if (pct <= 3) {
            currentRun++;
            if (currentRun > maxRunLength) maxRunLength = currentRun;
        } else {
            currentRun = 0;
        }
    }

    // For inside/outside strategy: mark odd-positioned slices in each run
    const placeInside = new Set();
    if (maxRunLength >= 2 && maxRunLength <= 3) {
        let inRun = false;
        let runPosition = 0;
        for (let i = 0; i < chartData.length; i++) {
            const pct = (chartData[i].value / total) * 100;
            if (pct <= 3) {
                if (inRun) {
                    runPosition++;
                } else {
                    inRun = true;
                    runPosition = 0;
                }
                if (runPosition % 2 === 1) {
                    placeInside.add(i);
                }
            } else {
                inRun = false;
            }
        }
    }

    const useTooltips = maxRunLength > 3;
    const useInsideOutside = maxRunLength >= 2 && maxRunLength <= 3;

    const chart = new Chart(ctx, {
        type: "doughnut",
        data: {
            labels: chartData.map((d) => d.label),
            datasets: [
                {
                    data: chartData.map((d) => d.value),
                    backgroundColor: colors,
                    borderWidth: 4,
                    hoverBorderColor: "transparent",
                    hoverBorderWidth: 0,
                    hoverOffset: 0,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            cutout: "69%",
            layout: {
                padding: 50, // Space for labels outside the donut
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: useTooltips ? {
                    enabled: true,
                    filter: (tooltipItem) => {
                        const data = tooltipItem.dataset.data;
                        const total = data.reduce((a, b) => a + b, 0);
                        const percentage = (data[tooltipItem.dataIndex] / total) * 100;
                        return percentage <= 3;
                    },
                    backgroundColor: "#211918",
                    titleFont: { family: "Space Mono, monospace", size: 12 },
                    bodyFont: { family: "Space Mono, monospace", size: 13 },
                    padding: 10,
                    displayColors: true,
                    callbacks: {
                        title: () => "",
                        label: (tooltipItem) => {
                            return ` ${tooltipItem.label}: ${tooltipItem.raw}%`;
                        },
                    },
                } : {
                    enabled: false,
                },
                datalabels: {
                    color: "#211918",
                    font: {
                        family: "Space Mono, monospace",
                        size: 14,
                        weight: "normal",
                    },
                    anchor: (context) => {
                        if (useInsideOutside && placeInside.has(context.dataIndex)) return "start";
                        return "end";
                    },
                    align: (context) => {
                        if (useInsideOutside && placeInside.has(context.dataIndex)) return "start";
                        return "end";
                    },
                    offset: (context) => {
                        if (useInsideOutside && placeInside.has(context.dataIndex)) return 10;
                        return 3;
                    },
                    display: (context) => {
                        if (!useTooltips) return true;
                        const data = context.dataset.data;
                        const total = data.reduce((a, b) => a + b, 0);
                        const percentage = (data[context.dataIndex] / total) * 100;
                        return percentage > 3;
                    },
                    formatter: (value) => {
                        return `${value}%`;
                    },
                },
            },
            animation: {
                animateRotate: true,
                animateScale: true,
                duration: 1000,
                easing: "easeOutQuart",
            },
        },
    });

    // Store chart instance using canvas id or index
    const chartId = canvas.id || `chart-${chartIndex}`;
    chartInstances.set(chartId, chart);

    // Setup legend interaction for this chart
    const container = canvas.closest(".portfolio-donut-content");
    if (container) {
        const legend = container.querySelector(".portfolio-donut-legend");
        if (legend) {
            const legendItems = legend.querySelectorAll(".portfolio-donut-legend-item");
            legendItems.forEach((item) => {
                const sliceIndex = parseInt(item.getAttribute("data-slice-index") || "0", 10);

                item.addEventListener("mouseenter", () => {
                    chart.setActiveElements([{ datasetIndex: 0, index: sliceIndex }]);
                    chart.update("none");
                });

                item.addEventListener("mouseleave", () => {
                    chart.setActiveElements([]);
                    chart.update("none");
                });
            });
        }
    }
}

function initDonutCharts() {
    const canvases = document.querySelectorAll(".portfolio-donut-canvas");
    const isBackend = document.body.classList.contains("neos-backend") || document.querySelector("[data-__neos-node-contextpath]");

    canvases.forEach((canvas, chartIndex) => {
        // In Neos backend, init immediately (no viewport animation)
        if (isBackend) {
            initSingleChart(canvas, chartIndex);
            initializedCanvases.add(canvas);
            return;
        }

        // Skip if already observed or initialized
        if (observedCanvases.has(canvas) || initializedCanvases.has(canvas)) {
            return;
        }

        // Observe for viewport entry
        observedCanvases.add(canvas);
        chartObserver.observe(canvas);
    });
}

// Force reinit for backend data changes
function reinitDonutCharts() {
    const canvases = document.querySelectorAll(".portfolio-donut-canvas");
    canvases.forEach((canvas, chartIndex) => {
        initSingleChart(canvas, chartIndex);
        initializedCanvases.add(canvas);
    });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initDonutCharts);
} else {
    initDonutCharts();
}

// Re-initialize on Swup page transitions
document.addEventListener("swup:contentReplaced", initDonutCharts);

// Listen for Neos backend content changes
if (typeof document.addEventListener === "function") {
    // Neos CMS fires this event when inline content is saved
    document.addEventListener("Neos.NodeCreated", () => setTimeout(reinitDonutCharts, 300));
    document.addEventListener("Neos.NodeUpdated", () => setTimeout(reinitDonutCharts, 300));
    document.addEventListener("Neos.ContentModuleLoaded", () => setTimeout(reinitDonutCharts, 300));

    // Also try the Neos UI event system
    if (window.NeosCMS && window.NeosCMS.event) {
        window.NeosCMS.event.on("contentChanged", () => setTimeout(reinitDonutCharts, 300));
    }
}

// Periodic check for Neos backend (fallback)
if (document.body.classList.contains("neos-backend") || document.querySelector("[data-__neos-node-contextpath]")) {
    setInterval(() => {
        const canvases = document.querySelectorAll(".portfolio-donut-canvas");
        canvases.forEach((canvas) => {
            const currentData = canvas.getAttribute("data-chart-data");
            const lastData = canvas._lastChartData;
            if (currentData !== lastData) {
                canvas._lastChartData = currentData;
                reinitDonutCharts();
            }
        });
    }, 1000);
}

// MutationObserver for dynamically added/changed charts (e.g., in Neos backend)
const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    mutations.forEach((mutation) => {
        // Check for added nodes
        if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
                if (
                    node.nodeType === 1 &&
                    ((node.classList && node.classList.contains("portfolio-donut-canvas")) ||
                        (node.querySelector && node.querySelector(".portfolio-donut-canvas")) ||
                        (node.classList && node.classList.contains("portfolio-donut-grid")) ||
                        (node.querySelector && node.querySelector(".portfolio-donut-grid")))
                ) {
                    shouldReinit = true;
                }
            });
        }
        // Check for attribute changes on canvas (data-chart-data)
        if (mutation.type === "attributes" && mutation.attributeName === "data-chart-data") {
            shouldReinit = true;
        }
        // Check for changes inside donut grid (Neos re-renders content)
        if (mutation.target && mutation.target.closest && mutation.target.closest(".portfolio-donut-grid")) {
            shouldReinit = true;
        }
    });
    if (shouldReinit) {
        const isBackend = document.body.classList.contains("neos-backend") || document.querySelector("[data-__neos-node-contextpath]");
        setTimeout(isBackend ? reinitDonutCharts : initDonutCharts, 150);
    }
});

observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-chart-data"],
});
